/**
 * MapClientChip: the chosen client, in each map's toolbar after the client
 * search, with a clear. One markup for both maps.
 *
 * Four states: a placed client ("<prefix> <Name>", with "Approximate
 * location" on a line of its own for a pin placed by town or ZIP); a
 * client not on the map ("<Name>: <reason>"); a client the read does not
 * carry ("Place this client to see them on the map."); and the read still loading
 * ("Finding the client"). Names are shown with the first letter
 * capitalised; the stored name is untouched. The text wraps; nothing is
 * cut. The clear never moves the map: the caller only forgets the client.
 * LP-43: given `href`, the name is a link to the client's record in the
 * house's link sage (the My Tasks rows' and the Directory's), in both
 * states that name the client.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import { APPROXIMATE_LOCATION_TEXT } from '@/lib/clientLocation';

export type MapClientChipState =
  | { kind: 'placed'; name: string; approximate: boolean }
  | { kind: 'not_placed'; name: string; reasonLabel: string }
  | { kind: 'unknown' }
  | { kind: 'loading' };

export interface MapClientChipProps {
  state: MapClientChipState;
  /** The words before a placed client's name: "Showing" or "Near". */
  placedPrefix: string;
  testId: string;
  clearTestId: string;
  onClear: () => void;
  /** A leading mark, e.g. the placement pin's colour as its legend. */
  icon?: ReactNode;
  /** The client's record; given, the name links to it. */
  href?: string;
  /** LP-43: `large` is the Directory's Near chip,
   *  at twice the size, so the name reads from across the map. */
  size?: 'base' | 'large';
}

const NAME_LINK_CLS =
  'rounded-tight font-medium text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function MapClientChip({ state, placedPrefix, testId, clearTestId, onClear, icon, href, size = 'base' }: MapClientChipProps): ReactNode {
  const large = size === 'large';
  const name = (raw: string): ReactNode =>
    href === undefined ? (
      capitalizeFirst(raw)
    ) : (
      <Link to={href} className={NAME_LINK_CLS} data-testid={`${testId}-name`}>
        {capitalizeFirst(raw)}
      </Link>
    );
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-card bg-cream text-sage-800 ${large ? 'gap-2.5 px-4 py-2 text-2xl' : 'gap-1.5 px-2.5 py-1 text-xs'}`}
      data-testid={testId}
    >
      {icon}
      <span className="min-w-0 break-words">
        {state.kind === 'placed' && (
          <>
            {`${placedPrefix} `}
            {name(state.name)}
            {state.approximate && (
              <>
                {' '}
                <span className="block text-ink-soft">{APPROXIMATE_LOCATION_TEXT}</span>
              </>
            )}
          </>
        )}
        {state.kind === 'not_placed' && (
          <>
            {name(state.name)}
            {`: ${state.reasonLabel}`}
          </>
        )}
        {state.kind === 'unknown' && 'Place this client to see them on the map.'}
        {state.kind === 'loading' && 'Finding the client'}
      </span>
      <Button
        variant="ghost"
        size="sm"
        icon={<X size={large ? 22 : 12} aria-hidden="true" />}
        onClick={onClear}
        aria-label="Clear the client"
        data-testid={clearTestId}
        className="!min-h-0 shrink-0 !px-1 !py-0"
      />
    </span>
  );
}
